import { useEffect, useRef } from 'react';

import { FormValidator, useForm } from '@/hooks/useForm';
import { TranslationsTypes, useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import Button from '@/decodo/components/atoms/Button';
import { Input } from '@/decodo/components/atoms/Input';
import { InputDropdown } from '@/decodo/components/atoms/InputDropdown';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';
import { TelephoneInput } from '@/decodo/components/molecules/TelephoneInput';
import { disallowedEmailDomains } from '@/utils/validation';

import { contactChannels, phoneCodes } from './constants';

export interface ContactSalesFormSchema {
  companyEmail?: string;
  companyName?: string;
  countryCode: string;
  companyPhone?: string;
  contactChannel: (typeof contactChannels)[number];
  weChat?: string;
  contactChannelId?: string;
  fullName?: string;
  useCase?: string;
  languageCode?: string;
}
const createValidator = (
  texts: TranslationsTypes,
  lang: string
): FormValidator<ContactSalesFormSchema> => ({
  fullName: (value) => {
    if (!value && lang === 'en')
      return texts.contact_sales.form.fields_errors.full_name;
  },
  companyEmail: (value) => {
    if (!value) return texts.contact_sales.form.fields_errors.company_email;
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return texts.contact_sales.form.fields_errors.invalid_email;
    if (disallowedEmailDomains.some((domain) => value.includes(`@${domain}`))) {
      return texts.contact_sales.form.fields_errors.invalid_email_domain;
    }
  },
  companyName: (value) => {
    if (!value) return texts.contact_sales.form.fields_errors.company_name;
  },
  useCase: (value) => {
    if (!value) return texts.contact_sales.form.fields_errors.use_case;
    if (value.length > 500)
      return texts.contact_sales.form.fields_errors.use_case_too_long;
  },
  countryCode: (value) => {
    if (
      value &&
      !/^\+\d{1,3}$/.test(value) &&
      !phoneCodes.includes(value as unknown as (typeof phoneCodes)[number])
    )
      return texts.contact_sales.form.fields_errors.country_code;
  },
  companyPhone: (value) => {
    if (!value && lang === 'zh-cn')
      return texts.contact_sales.form.fields_errors.company_phone;
    if (value && !/^\d{6,}$/.test(value))
      return texts.contact_sales.form.fields_errors.company_phone;
  },
});

export function ContactSalesForm(props: {
  onSubmit: (data: ContactSalesFormSchema) => void;
  loading?: boolean;
}) {
  const { texts, lang } = useTranslation();
  const validator = createValidator(texts, lang);

  const formRef = useRef<HTMLFormElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  const {
    handleSubmit,
    getFieldProps,
    setFieldValue,
    isValid: isFormValid,
  } = useForm<ContactSalesFormSchema>({
    initialValues: {
      fullName: '',
      companyName: '',
      companyEmail: '',
      contactChannel: lang === 'zh-cn' ? 'QQ' : 'WhatsApp',
      contactChannelId: '',
      countryCode: '+1',
      companyPhone: '',
      useCase: '',
      weChat: '',
      languageCode: '',
    },
    validator,
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    if (formRef.current && footerRef.current) {
      const FOOTER_PADDING_TOP = 8;
      const TEXTAREA_PADDING_BOTTOM = 16;
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            if (!formRef.current) return;
            formRef.current.style.setProperty(
              'padding-bottom',
              `${
                entry.borderBoxSize[0].blockSize -
                FOOTER_PADDING_TOP -
                TEXTAREA_PADDING_BOTTOM
              }px`
            );
          }
        }
      });

      resizeObserver.observe(footerRef.current);

      return () => {
        if (!footerRef.current) return;
        resizeObserver.unobserve(footerRef.current);
      };
    }
  }, []);

  return (
    <form
      onSubmit={handleSubmit(props.onSubmit)}
      className='mb-8 grid grid-cols-1 items-start gap-x-6 gap-y-2 md:mb-0 md:gap-y-4 lg:grid-cols-2'
      data-tracker-component='form'
      ref={formRef}
    >
      <div className=''>
        <Input
          label={texts.contact_sales.form.fields.full_name}
          isRequired={lang === 'en'}
          {...getFieldProps('fullName')}
        />
      </div>
      <div className=''>
        <Input
          label={texts.contact_sales.form.fields.company_name}
          isRequired
          {...getFieldProps('companyName')}
        />
      </div>
      <div className=''>
        <Input
          label={texts.contact_sales.form.fields.company_email}
          isRequired
          {...getFieldProps('companyEmail')}
        />
      </div>
      <div className='flex flex-row items-end gap-x-2'>
        <InputDropdown
          classNames={{
            root: 'max-w-[140px]',
          }}
          popoverProps={{
            triggerProps: {
              'data-tracker-context': 'contact_channel',
            },
            contentProps: {
              avoidCollisions: true,
            },
          }}
          label={texts.contact_sales.form.fields.social_network}
          options={contactChannels.map((channel) => ({
            label: channel,
            value: channel,
          }))}
          {...getFieldProps('contactChannel')}
          onOptionChange={(value) => setFieldValue('contactChannel', value)}
        />
        <Input
          {...getFieldProps('contactChannelId')}
          classNames={{ root: 'flex-grow h-14' }}
        />
      </div>

      <TelephoneInput
        countryCodeProps={{
          ...getFieldProps('countryCode'),
          onOptionChange: (value) => setFieldValue('countryCode', value),
          isRequired: lang === 'zh-cn',
          label: texts.contact_sales.form.fields.phone_number,
          popoverProps: {
            triggerProps: {
              'data-tracker-context': 'country_code',
            },
          },
        }}
        phoneNumberProps={{
          ...getFieldProps('companyPhone'),
          classNames: { root: 'h-14' },
        }}
      />
      {lang === 'zh-cn' && (
        <div className=''>
          <Input
            label={texts.contact_sales.form.fields.wechat}
            {...getFieldProps('weChat')}
          />
        </div>
      )}

      <div className='lg:col-span-2'>
        <Input
          label={texts.contact_sales.form.fields.use_case}
          {...getFieldProps('useCase')}
          isRequired
          maxLength={500}
          data-tracker-context='use_case'
          as='textarea'
          classNames={{
            input: {
              inputElement: 'min-h-[80px] h-[80px]',
            },
          }}
        />
      </div>
      <div
        className={cn(
          'absolute bottom-0',
          'left-0 right-0 md:left-auto md:right-auto',
          'pl-5 pr-5 md:pl-0',
          'bg-base-white grid grid-cols-1 flex-col gap-6 pb-12 pt-2 font-mono md:pb-10 md:pr-10 lg:col-span-2 lg:grid-cols-2'
        )}
        data-tracker-component='footer'
        ref={footerRef}
      >
        <Text variant='body-s' color='gray-dark-500'>
          {texts.contact_sales.form.process_data}{' '}
          <LinkButton
            href='/legal'
            variant='text'
            slim
            color='tertiary'
            classNames={{ root: 'text-body-s underline' }}
          >
            {texts.general.privacy_policy}.
          </LinkButton>
        </Text>
        <Button
          type='submit'
          classNames={{ root: 'md:w-auto w-full' }}
          disabled={props.loading || !isFormValid}
        >
          {texts.contact_sales.form.submit}
        </Button>
      </div>
    </form>
  );
}
